import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SelectionStatus, UniversalMediaList } from '@/app/features/universal-media-lists/models';
import { UniversalMediaListsService } from '@/app/features/universal-media-lists/_services/universal-media-lists.service';
import {
  AppFilter,
  AppTable,
  AppTableColumn,
  AudienceTargetingGroup,
  AudienceTargetingSegment,
  CommonDataModel,
  PortalType,
  ViewMode
} from '../../_models/models';
import { SharedService } from '../../_services/shared.service';
import {
  universalMediaContentTable,
  getUniversalMediaContentFilters,
  getUniversalMediaContentTableColumns,
  UNIVERSAL_MEDIA_CONTENT_MESSAGES
} from '../universal-media-domains/universal-media-domains.config';

@Component({
  selector: 'app-universal-media-list-container',
  templateUrl: './universal-media-list-container.component.html',
  styleUrls: ['./universal-media-list-container.component.less']
})
export class UniversalMediaListContainerComponent implements OnInit, OnDestroy {
  readonly UNIVERSAL_MEDIA_CONTENT_MESSAGES = UNIVERSAL_MEDIA_CONTENT_MESSAGES;

  @Input() universalMediaLists: UniversalMediaList[] = [];
  @Input() universalMediaListsMetaData: CommonDataModel<UniversalMediaList[]>;
  @Input() universalMediaListModalFilters: AppFilter[] = [];
  @Input() isDataLoading = false;
  @Input() portalType: PortalType;
  @Input() rxDealMode = false;
  @Input() segmentsToAdd: AudienceTargetingSegment[] = [];
  @Input() groups: AudienceTargetingGroup[] = [];

  @Output() selected = new EventEmitter<UniversalMediaList>();
  @Output() filtersChanged = new EventEmitter<{ filters: AppFilter[]; filterId: string }>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() viewModeChanged = new EventEmitter<boolean>();

  viewMode: ViewMode = ViewMode.SELECTION;
  selectedUniversalMediaListForView: UniversalMediaList = null;

  universalMediaContentList: any[] = [];
  universalMediaContentMetaData: CommonDataModel<any[]>;
  universalMediaContentFilters: AppFilter[] = [];
  universalMediaContentTable: AppTable;
  universalMediaContentTableColumns: AppTableColumn[] = [];

  private unsubscribe$ = new Subject<void>();
  private universalMediaListsService = inject(UniversalMediaListsService);
  private sharedService = inject(SharedService);

  ngOnInit(): void {
    this.universalMediaContentTable = universalMediaContentTable;

    this.universalMediaContentFilters = getUniversalMediaContentFilters();
    this.universalMediaContentTableColumns = getUniversalMediaContentTableColumns();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onUniversalMediaListSelected(universalMediaList: UniversalMediaList): void {
    this.selected.emit(universalMediaList);
  }

  onUniversalMediaListViewRequested(universalMediaList: UniversalMediaList): void {
    this.selectedUniversalMediaListForView = universalMediaList;

    this.universalMediaContentMetaData = new CommonDataModel<any[]>([], 0, true, true, false, null);
    this.universalMediaContentList = [];

    this.universalMediaContentTable.pageIndex = 1;

    this.viewMode = ViewMode.VIEW;
    this.viewModeChanged.emit(true);
    this.loadUniversalMediaContent();
  }

  getUniversalMediaListStatus(universalMediaList: UniversalMediaList): SelectionStatus {
    const isSelected = this.segmentsToAdd?.some(segment => segment.universalMediaList?.id === universalMediaList.id);
    if (isSelected) {
      return SelectionStatus.SELECTED;
    }
    const isAlreadyInDeal = this.groups?.some(group =>
      group.segments?.some(segment => segment.universalMediaList?.id === universalMediaList.id)
    );
    if (isAlreadyInDeal) {
      return SelectionStatus.DISABLED;
    }

    return null;
  }

  onFiltersChanged(event: { filters: AppFilter[]; filterId: string }): void {
    this.filtersChanged.emit(event);
  }

  onPageChanged(pageIndex: number): void {
    this.pageChanged.emit(pageIndex);
  }

  backToSelection(): void {
    this.viewMode = ViewMode.SELECTION;
    this.selectedUniversalMediaListForView = null;
    this.universalMediaContentList = [];
    this.universalMediaContentMetaData = null;

    this.universalMediaContentTable.pageIndex = 1;

    this.viewModeChanged.emit(false);
  }

  onUniversalMediaContentSearch(event: { filters: AppFilter[]; filterId: string }): void {
    const searchFilter = event.filters?.find(f => f.id === 'mediaContentSearch');
    const searchTerm = searchFilter?.selectedValues || '';
    this.universalMediaContentTable.pageIndex = 1;

    if (this.universalMediaContentMetaData) {
      this.universalMediaContentMetaData.isLoading = true;
    }

    this.loadUniversalMediaContent(searchTerm);
  }

  onUniversalMediaContentPageChanged(tableEvent: AppTable): void {
    const pageIndex = tableEvent.pageIndex;
    this.universalMediaContentTable.pageIndex = pageIndex;
    const searchFilter = this.universalMediaContentFilters?.find(f => f.id === 'mediaContentSearch');
    const searchTerm = searchFilter?.selectedValues?.[0]?.value || '';

    if (this.universalMediaContentMetaData) {
      this.universalMediaContentMetaData.isLoading = true;
    }

    this.loadUniversalMediaContent(searchTerm, pageIndex);
  }

  onUniversalMediaContentSortChanged(tableEvent: AppTable): void {
    this.universalMediaContentTable.sortBy = tableEvent.sortBy;
    this.universalMediaContentTable.sortDirection = tableEvent.sortDirection;
    this.universalMediaContentTable.pageIndex = 1;

    const searchFilter = this.universalMediaContentFilters?.find(f => f.id === 'mediaContentSearch');
    const searchTerm = searchFilter?.selectedValues?.[0]?.value || '';

    if (this.universalMediaContentMetaData) {
      this.universalMediaContentMetaData.isLoading = true;
    }

    this.loadUniversalMediaContent(searchTerm, 1);
  }

  private loadUniversalMediaContent(searchTerm: string = '', pageIndex: number = 1): void {
    if (!this.selectedUniversalMediaListForView) {
      return;
    }

    this.universalMediaContentMetaData.isLoading = true;
    const requestParams = this.createUniversalMediaListLoaderQueryParams(searchTerm, pageIndex);

    this.universalMediaListsService.getUniversalMediaListDomains(
      this.selectedUniversalMediaListForView.id,
      requestParams
    ).pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe({
      next: (response) => this.universalMediaContentResponseHandler(response),
      error: (error) => this.universalMediaContentResponseErrorHandler(error)
    });
  }

  private createUniversalMediaListLoaderQueryParams(searchTerm: string = '', pageIndex: number = 1) {
    return {
      page: pageIndex,
      limit: this.universalMediaContentTable.pageSize,
      ...(searchTerm && {
        search: { matchingValue: { $contL: `%${searchTerm}%` } }
      })
    };
  }

  private universalMediaContentResponseHandler(response: CommonDataModel<any[]>): void {
    const isInitialLoadingState = response.status === 'Fetching' ||
          (response.isLoading && response.totalElements === 0 && (!response.data || response.data.length === 0));

    if (isInitialLoadingState) {
      return;
    }

    const transformedData = (response.data || []).map((item: any, index: number) => ({
      id: item.segmentId || index,
      domainBundle: item.matchingValue?.trim() || '',
      segmentId: item.segmentId,
      originalData: item
    }));

    this.universalMediaContentList = transformedData;

    this.universalMediaContentMetaData.data = this.universalMediaContentList;
    this.universalMediaContentMetaData.totalElements = response.totalElements || 0;
    this.universalMediaContentMetaData.isLoading = false;
    this.universalMediaContentMetaData.isPaginated = response.isPaginated || false;
    this.universalMediaContentMetaData.isPreparingForDownload = false;
    this.universalMediaContentMetaData.httpErr = null;
  }

  private universalMediaContentResponseErrorHandler(error: any): void {
    this.sharedService.showNotification('error', 'Error', UNIVERSAL_MEDIA_CONTENT_MESSAGES.loadError);

    this.universalMediaContentMetaData.data = [];
    this.universalMediaContentMetaData.totalElements = 0;
    this.universalMediaContentMetaData.isLoading = false;
    this.universalMediaContentMetaData.isPaginated = false;
    this.universalMediaContentMetaData.isPreparingForDownload = false;
    this.universalMediaContentMetaData.httpErr = error;
    this.universalMediaContentList = [];
  }
}
